/**
 * usePresence - Generic Socket.IO hook for real-time presence tracking + live data sync
 * 
 * This is a GENERIC presence hook that can be used on ANY page, not just matrices.
 * It tracks who is currently viewing a resource (identified by resourceType + resourceId).
 * 
 * LIVE DATA SYNC:
 * Pass an onDataChange callback to receive real-time data change events from other users.
 * The returned socketId should be sent as 'x-socket-id' header on API calls so the
 * server can exclude the sender from broadcast (avoiding echo).
 * 
 * DESIGN PRINCIPLES:
 * - Server handles all room management and cleanup
 * - Multiple browser tabs stay present (one socket per tab)
 * - Background tabs stay listed as status 'away' (gray avatars)
 * - Heartbeat keeps running even when the tab is hidden
 * - Client is simple: connect, join, listen for updates
 * - Cleanup is robust: uses refs instead of closure variables
 * 
 * USAGE:
 *   // Basic presence only
 *   const { viewers, isConnected } = usePresence('matrix', `${projectId}:piping`);
 * 
 *   // Presence + live data sync
 *   const { viewers, isConnected, socketId } = usePresence('rentals', 'rentals-view', true, (change) => {
 *     // change = { action, rowId, fields, updatedBy, timestamp, ... }
 *     if (change.action === 'cell-updated') updateCell(change.rowId, change.fields);
 *     if (change.action === 'row-added') refreshData();
 *     if (change.action === 'row-deleted') refreshData();
 *   });
 *   // Send socketId on API calls: headers: { 'x-socket-id': socketId }
 * 
 * @param {string} resourceType - Type of resource (e.g., 'matrix', 'document', 'report')
 * @param {string} resourceId - Unique identifier for the resource
 * @param {boolean} enabled - Whether presence tracking is enabled
 * @param {Function} [onDataChange] - Callback for live data change events from other users
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { io } from 'socket.io-client';
import tokenManager from '../utils/tokenManager';
import * as portalSession from '../auth/portalSession';

// Socket.IO server URL
const getSocketUrl = () => {
  // Allow explicit override
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }

  // Port-based mapping (works in both dev + docker prod builds)
  // - Standard dev-env: frontend 3002 -> backend 3003
  // - Docker feature env: frontend 3004 -> backend 3005
  if (typeof window !== 'undefined') {
    const port = window.location.port;
    if (port === '3004') return 'http://localhost:3005';
    if (port === '3002') return 'http://localhost:3003';
    if (port === '3001') return 'http://localhost:3000';
  }

  // In production, default to same origin (reverse-proxied)
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }

  return 'http://localhost:3003';
};

/**
 * Generic presence tracking hook
 * 
 * @param {string} resourceType - Type of resource being viewed
 * @param {string} resourceId - Unique ID of the resource
 * @param {boolean} enabled - Whether to enable presence tracking
 * @returns {Object} { viewers, isConnected, connectionState, error }
 */
const usePresence = (resourceType, resourceId, enabled = true, onDataChange = null) => {
  const { instance, accounts } = useMsal();
  const [viewers, setViewers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [error, setError] = useState(null);
  const [socketId, setSocketId] = useState(null);
  
  // Stable refs - these persist across renders and are used in cleanup
  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);
  const mountedRef = useRef(false);
  const resourceRef = useRef({ resourceType, resourceId });
  const accountsRef = useRef(accounts);
  const connectAttemptRef = useRef(0);
  const viewersRef = useRef(viewers);
  const onDataChangeRef = useRef(onDataChange);
  
  // Keep accountsRef updated without retriggering socket lifecycle
  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  // Keep viewersRef updated for async callbacks
  useEffect(() => {
    viewersRef.current = viewers;
  }, [viewers]);

  // Keep onDataChangeRef updated without retriggering socket lifecycle
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // Track actual mount/unmount (NOT effect lifecycle)
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Keep resourceRef updated
  useEffect(() => {
    resourceRef.current = { resourceType, resourceId };
  }, [resourceType, resourceId]);
  
  // Unique ID for debugging
  const hookIdRef = useRef(Math.random().toString(36).substr(2, 6));
  const hookId = hookIdRef.current;
  
  /**
   * Get auth token
   */
  const getToken = useCallback(async () => {
    const accs = accountsRef.current;
    if ((!accs || accs.length === 0) && !portalSession.isActive()) {
      throw new Error('No authenticated user');
    }
    return await tokenManager.getToken(instance, accs);
  }, [instance]);
  
  /**
   * Cleanup function - can be called from effect cleanup or beforeunload
   * Uses refs to ensure we always have the correct values
   */
  const cleanup = useCallback(() => {
    const socket = socketRef.current;
    const { resourceType: rt, resourceId: rid } = resourceRef.current;
    
    console.log(`[Presence ${hookId}] Cleanup called for ${rt}/${rid}, socket: ${socket?.id}, connected: ${socket?.connected}`);
    
    // Stop heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    
    // Leave room and disconnect
    if (socket) {
      if (socket.connected && rt && rid) {
        console.log(`[Presence ${hookId}] Emitting presence:leave for ${rt}/${rid}`);
        socket.emit('presence:leave', { resourceType: rt, resourceId: rid });
      }
      socket.disconnect();
      socketRef.current = null;
    }
  }, [hookId]);
  
  /**
   * Main effect - handles socket lifecycle
   */
  useEffect(() => {
    // Skip if disabled or missing params
    if (!enabled || !resourceType || !resourceId) {
      return;
    }

    // Increment connect attempt id so we can cancel stale in-flight connects
    const attemptId = ++connectAttemptRef.current;
    let cancelled = false;

    const connect = async () => {
      try {
        console.log(`[Presence ${hookId}] Connecting for ${resourceType}/${resourceId}`);
        setConnectionState('connecting');
        
        const token = await getToken();
        if (cancelled || attemptId !== connectAttemptRef.current || !mountedRef.current) {
          return;
        }
        
        // Create new socket
        const socket = io(getSocketUrl(), {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
        });
        
        // Store in ref immediately for cleanup access
        socketRef.current = socket;
        
        socket.on('connect', () => {
          if (cancelled || attemptId !== connectAttemptRef.current || !mountedRef.current) {
            console.log(`[Presence ${hookId}] Connected but unmounted, disconnecting`);
            socket.disconnect();
            return;
          }
          console.log(`[Presence ${hookId}] Connected, socket: ${socket.id}`);
          setIsConnected(true);
          setConnectionState('connected');
          setSocketId(socket.id);
          setError(null);
          
          // Join the room - server handles cleanup of old rooms automatically
          const initialStatus = typeof document !== 'undefined' && document.hidden ? 'away' : 'active';
          console.log(`[Presence ${hookId}] Emitting presence:join for ${resourceType}/${resourceId} (${initialStatus})`);
          socket.emit('presence:join', { resourceType, resourceId, status: initialStatus });

          // Safety net: if we don't receive viewers quickly, re-emit join once.
          // This helps if the first presence:viewers event is missed during initial load.
          setTimeout(() => {
            if (cancelled || attemptId !== connectAttemptRef.current || !mountedRef.current) return;
            if (socketRef.current !== socket) return;
            if (!socket.connected) return;
            if (viewersRef.current && viewersRef.current.length > 0) return;
            console.log(`[Presence ${hookId}] No viewers received yet, re-emitting presence:join for ${resourceType}/${resourceId}`);
            const retryStatus = typeof document !== 'undefined' && document.hidden ? 'away' : 'active';
            socket.emit('presence:join', { resourceType, resourceId, status: retryStatus });
          }, 1000);
          
          // Keep heartbeat running in background tabs so Redis TTL does not expire.
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
          }
          heartbeatRef.current = setInterval(() => {
            if (socket.connected && mountedRef.current) {
              const status = typeof document !== 'undefined' && document.hidden ? 'away' : 'active';
              socket.emit('presence:heartbeat', { resourceType, resourceId, status });
            }
          }, 30000);
        });
        
        socket.on('disconnect', (reason) => {
          if (!mountedRef.current) return;
          console.log(`[Presence ${hookId}] Disconnected: ${reason}`);
          setIsConnected(false);
          setConnectionState('disconnected');
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
          }
        });
        
        socket.on('connect_error', (err) => {
          if (!mountedRef.current) return;
          console.error(`[Presence ${hookId}] Connection error:`, err.message);
          setConnectionState('error');
          setError(err.message);
        });
        
        // Presence events - check both resourceType and resourceId match
        socket.on('presence:viewers', (data) => {
          if (!mountedRef.current || cancelled || attemptId !== connectAttemptRef.current) return;
          if (data.resourceType !== resourceType || data.resourceId !== resourceId) {
            console.log(
              `[Presence ${hookId}] Ignoring presence:viewers for ${data.resourceType}/${data.resourceId} (current ${resourceType}/${resourceId})`
            );
            return;
          }
          console.log(`[Presence ${hookId}] presence:viewers -> ${(data.viewers || []).length} viewer(s)`);
          setViewers(data.viewers || []);
        });
        
        socket.on('presence:joined', (data) => {
          if (!mountedRef.current || cancelled || attemptId !== connectAttemptRef.current) return;
          if (data.resourceType !== resourceType || data.resourceId !== resourceId) return;
          console.log(`[Presence ${hookId}] presence:joined -> ${(data.viewers || []).length} viewer(s)`);
          setViewers(data.viewers || []);
        });
        
        socket.on('presence:left', (data) => {
          if (!mountedRef.current || cancelled || attemptId !== connectAttemptRef.current) return;
          if (data.resourceType !== resourceType || data.resourceId !== resourceId) return;
          console.log(`[Presence ${hookId}] presence:left -> ${(data.viewers || []).length} viewer(s)`);
          setViewers(data.viewers || []);
        });
        
        socket.on('presence:error', (data) => {
          if (!mountedRef.current || cancelled || attemptId !== connectAttemptRef.current) return;
          console.error(`[Presence ${hookId}] Error:`, data.message);
          setError(data.message);
        });
        
        // Live data sync — forward data:changed events to the caller's callback
        socket.on('data:changed', (data) => {
          if (!mountedRef.current || cancelled || attemptId !== connectAttemptRef.current) return;
          if (data.resourceType !== resourceType || data.resourceId !== resourceId) return;
          if (onDataChangeRef.current) {
            onDataChangeRef.current(data);
          }
        });
        
      } catch (err) {
        if (!cancelled && attemptId === connectAttemptRef.current && mountedRef.current) {
          console.error(`[Presence ${hookId}] Failed to connect:`, err);
          setConnectionState('error');
          setError(err.message);
        }
      }
    };
    
    connect();
    
    // Cleanup function - runs when component unmounts or deps change
    return () => {
      console.log(`[Presence ${hookId}] Effect cleanup for ${resourceType}/${resourceId}`);
      cancelled = true;
      cleanup();
    };
  }, [enabled, resourceType, resourceId, getToken, hookId, cleanup]);
  
  /**
   * Visibility: stay present in the background (gray / away), re-join when focused.
   */
  useEffect(() => {
    if (!enabled) return;

    const emitStatus = (status, { rejoin = false } = {}) => {
      const socket = socketRef.current;
      const { resourceType: rt, resourceId: rid } = resourceRef.current;
      if (!socket?.connected || !rt || !rid) return;
      if (rejoin) {
        socket.emit('presence:join', { resourceType: rt, resourceId: rid, status });
      }
      socket.emit('presence:status', { resourceType: rt, resourceId: rid, status });
      socket.emit('presence:heartbeat', { resourceType: rt, resourceId: rid, status });
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        emitStatus('away');
      } else {
        emitStatus('active', { rejoin: true });
      }
    };

    const handlePageShow = () => {
      emitStatus(document.hidden ? 'away' : 'active', { rejoin: true });
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [enabled]);
  
  /**
   * Handle page unload - ensure we leave the room
   */
  useEffect(() => {
    if (!enabled) return;
    
    const handleBeforeUnload = () => {
      console.log(`[Presence ${hookId}] beforeunload - calling cleanup`);
      cleanup();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, cleanup, hookId]);
  
  return {
    viewers,
    isConnected,
    connectionState,
    error,
    socketId,
  };
};

export default usePresence;
